// pages/VerifyEmailSuccess.tsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useVerifyAccount } from "../../lib/api/authOnboarding";
import { Loader2, CheckCircle, XCircle, ArrowRight } from "lucide-react";
import logo from "../../assets/logo.png";
import signinImg from "../../assets/signin.png";

const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL;
export default function VerifyEmailSuccess() {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);
  const navigate = useNavigate();
  const { mutateAsync: verifyAccount } = useVerifyAccount();
  const { id, token } = useParams();

  const [isVerified, setIsVerified] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [countdown, setCountdown] = useState(5);

  const hasCalled = useRef(false);

  useEffect(() => {
    if (hasCalled.current || !id || !token) return;
    hasCalled.current = true;

    const verify = async () => {
      try {
        await verifyAccount({ id: id as string, token: token as string });
        setIsVerified(true);
        setIsLoading(false);
      } catch (error) {
        setHasError(true);
        setIsLoading(false);
      }
    };

    verify();
  }, [id, token, verifyAccount]);

  // Countdown and redirect after successful verification
  useEffect(() => {
    if (!isVerified) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate("/login");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isVerified, navigate]);

  return (
    <div className="h-screen flex overflow-hidden">
      {/* Left Side - Image (Fixed) */}
      <div className="hidden lg:block lg:w-1/2 xl:w-[55%] relative h-screen">
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#8B52FF]/90 to-[#E05A6A]/90 z-10" />

        {/* Image */}
        <img
          src={signinImg}
          alt="Verify Email"
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* Content Overlay */}
        <div className="absolute inset-0 z-20 flex flex-col justify-between p-12 xl:p-16">
          <div></div>
          <div className="max-w-lg">
            <h2 className="text-4xl xl:text-5xl font-bold text-white mb-6 leading-tight">
              {isLoading
                ? "Verifying Your Account"
                : isVerified
                  ? "Welcome to Unimerg!"
                  : "Verification Issue"}
            </h2>
            <p className="text-lg text-white/80 leading-relaxed">
              {isLoading
                ? "Please wait while we confirm your email address."
                : isVerified
                  ? "Your email has been verified. You're all set to start your journey."
                  : "We encountered an issue verifying your account. Please try again."}
            </p>
          </div>
          <div className="flex items-center gap-8">
            <div>
              <div className="text-3xl font-bold text-white">50K+</div>
              <div className="text-white/70 text-sm">Contributors</div>
            </div>
            <div className="w-px h-12 bg-white/20" />
            <div>
              <div className="text-3xl font-bold text-white">10+</div>
              <div className="text-white/70 text-sm">Countries</div>
            </div>
            <div className="w-px h-12 bg-white/20" />
            <div>
              <div className="text-3xl font-bold text-white">$1M+</div>
              <div className="text-white/70 text-sm">Paid Out</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Content */}
      <div className="flex-1 flex flex-col bg-white h-screen">
        {/* Fixed Header */}
        <div className="flex-shrink-0 p-6 lg:p-8 border-b border-gray-100">
          <Link to={FRONTEND_URL as string}>
            <img src={logo} alt="Unimerg" className="h-9 w-auto" />
          </Link>
        </div>

        {/* Content Container */}
        <div className="flex-1 overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-400">
          <div className="flex-1 flex items-center justify-center px-6 lg:px-12 xl:px-20 py-8">
            <div className="w-full max-w-sm text-center">
              {/* Loading State */}
              {isLoading && (
                <>
                  {/* Loading Icon */}
                  <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-[#8B52FF] to-[#E05A6A] flex items-center justify-center shadow-lg shadow-[#8B52FF]/30 mb-8">
                    <Loader2 className="w-10 h-10 text-white animate-spin" />
                  </div>

                  {/* Header */}
                  <h1 className="text-3xl font-bold text-gray-900 mb-3">
                    Verifying Your Email
                  </h1>
                  <p className="text-gray-500 leading-relaxed mb-8">
                    Please wait while we confirm your email address. This will
                    only take a moment.
                  </p>

                  {/* Progress Indicator */}
                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] rounded-full animate-pulse"
                      style={{ width: "60%" }}
                    />
                  </div>
                </>
              )}

              {/* Success State */}
              {!isLoading && isVerified && !hasError && (
                <>
                  {/* Success Icon */}
                  <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center shadow-lg shadow-green-500/30 mb-8">
                    <CheckCircle className="w-10 h-10 text-white" />
                  </div>

                  {/* Header */}
                  <h1 className="text-3xl font-bold text-gray-900 mb-3">
                    Email Verified!
                  </h1>
                  <p className="text-gray-500 leading-relaxed mb-8">
                    Your account has been successfully verified. You can now log
                    in and start using Unimerg.
                  </p>

                  {/* Countdown */}
                  <div className="mb-6 p-4 bg-gray-50 rounded-xl border border-gray-100">
                    <p className="text-sm text-gray-600">
                      Redirecting to login in{" "}
                      <span className="font-bold text-[#8B52FF]">
                        {countdown}
                      </span>{" "}
                      seconds...
                    </p>
                  </div>

                  {/* Go to Login Button */}
                  <button
                    onClick={() => navigate("/login")}
                    className="w-full py-3.5 bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white font-semibold rounded-xl shadow-lg shadow-[#8B52FF]/25 hover:shadow-xl hover:shadow-[#8B52FF]/30 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 flex items-center justify-center gap-2 group"
                  >
                    Go to Login Now
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
                  </button>
                </>
              )}

              {/* Error State */}
              {!isLoading && hasError && (
                <>
                  {/* Error Icon */}
                  <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-red-400 to-rose-500 flex items-center justify-center shadow-lg shadow-red-500/30 mb-8">
                    <XCircle className="w-10 h-10 text-white" />
                  </div>

                  {/* Header */}
                  <h1 className="text-3xl font-bold text-gray-900 mb-3">
                    Verification Failed
                  </h1>
                  <p className="text-gray-500 leading-relaxed mb-8">
                    The verification link may have expired or is invalid. Please
                    request a new verification email or contact support.
                  </p>

                  {/* Action Buttons */}
                  <div className="space-y-3">
                    <button
                      onClick={() => navigate("/signup")}
                      className="w-full py-3.5 bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white font-semibold rounded-xl shadow-lg shadow-[#8B52FF]/25 hover:shadow-xl hover:shadow-[#8B52FF]/30 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 flex items-center justify-center gap-2 group"
                    >
                      Back to Sign Up
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
                    </button>

                    <button
                      onClick={() => navigate("/login")}
                      className="w-full py-3.5 bg-white text-gray-700 font-semibold rounded-xl border-2 border-gray-200 hover:border-[#8B52FF] hover:text-[#8B52FF] transition-all duration-200"
                    >
                      Go to Login
                    </button>
                  </div>

                  {/* Support Link */}
                  <p className="mt-6 text-sm text-gray-500">
                    Need help?{" "}
                    <Link
                      to="/help"
                      className="text-[#8B52FF] hover:text-[#E05A6A] font-medium transition-colors"
                    >
                      Contact Support
                    </Link>
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
